import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { programs } from '@cwos/api-client';
import { ConsequenceNote, SkeletonSet } from '@cwos/ui-admin';
import { SettingsActions, SettingsCard, SettingsField } from '../settings/common.js';
import { useNav } from '../navigation.js';
import {
  ATTESTED_OVERRIDE_LABEL,
  ATTESTED_OVERRIDE_OFF_WARNING,
  BREADCRUMB_COLLECTION_WHEN,
  BREADCRUMBS_OFF_WARNING,
  breadcrumbsOnWarning,
  CLOCK_COLLECTION_WHEN,
  CLOCK_REQUIRED_HONESTY,
  EXCEPTION_APPROVAL_LABEL,
  EXCEPTION_APPROVAL_OFF_NOTE,
  EXCEPTION_APPROVAL_ON_NOTE,
  EXCEPTION_INTEGRITY_SENTENCE,
  EXCEPTION_REASON_LABEL,
  EXCEPTION_REASON_ON_NOTE,
  MODE_OFF_TRACKING_NOTE,
  PROGRAM_BACK_LABEL,
  PROGRAM_SAVE_LABEL,
  programAudience,
  programInheritsNote,
  programPolicyHeading,
  programScopeNote,
  PURGE_JOB_NAME,
  PURGE_SWEEP_SENTENCE,
  STRICTER_MODES,
  stricterModeNote,
  workerBreadcrumbCardQuote,
} from './copy.js';
import { LocationSettingsShell } from './LocationSettingsShell.js';
import { ModeCards } from './ModeCards.js';
import { PolicySchedule } from './PolicySchedule.js';
import { scheduleStatementOf, usePolicyForm, validateDraft } from './policyForm.js';
import { LoadFailure } from '../LoadFailure.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE PROGRAM'S LOCATION POLICY — THE SAME SURFACE THE ORGANIZATION HAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * THE RULING (founder, REVREL follow-up 1, 2026-09-08)
 *
 *   "Do not leave program overrides permanently uneditable while a future
 *    revision exists. Give the program-override surface the same explicit
 *    relationship semantics required elsewhere: keep · explicitly
 *    replace/supersede · explicitly cancel · create a valid sequential future
 *    revision where supported."
 *
 *   "The current disabled state is acceptable ONLY AS A TEMPORARY FAIL-CLOSED
 *    CONDITION until that customer-facing control is implemented."
 *
 *   "Program overrides are first-class policy configuration and MUST NOT BECOME
 *    A WEAKER SCHEDULING SURFACE THAN ORGANIZATION-LEVEL POLICY."
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT THE PROGRAM SURFACE WAS, AND WHY THE GAP WAS NOT MERELY "NO SCHEDULE"
 * ═══════════════════════════════════════════════════════════════════════════
 * A row in a list: a mode dropdown, an Update button, a Remove button. It could
 * express ONE of the ten values `location_policy` carries and none of the acts.
 * It could not set an interval, an accuracy floor, a retention window, the
 * clock-punch questions or the exception rules; it could not schedule; and once
 * anything was waiting on the program's scope it disabled itself, because a
 * dropdown cannot state a relationship to a scheduled revision and REVREL was
 * right not to send a body the server answers 409 to.
 *
 * So the fix is not a date picker bolted to a row. It is THE SAME FORM: this
 * page is `usePolicyForm(programId)` — the identical hook the four organization
 * pages use, with the scope changed — rendering the identical controls and the
 * identical `PolicySchedule`. There is no second relationship vocabulary, no
 * second validation, no second wire body, and no second set of sentences that
 * could drift from the organization's. That is the ruling's "not a weaker
 * surface" spelled as CODE REUSE rather than as a promise.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE ONE ITEM THAT CANNOT APPLY AT PROGRAM SCOPE, AND WHY
 * ═══════════════════════════════════════════════════════════════════════════
 * The per-program override LIST — "Everyone (organization default)" plus a row
 * per program — has no program-scoped counterpart, because the precedence is
 * two layers deep and not recursive: `resolvePolicy` is program → organization →
 * frozen product default (`apps/api/src/domains/location/policy.ts`). A program
 * has nothing below it to override, so a list of its overrides would be a
 * control with no rows it could ever grow. It is ABSENT rather than empty.
 *
 * Everything else the organization surface expresses is here. `minAccuracyMeters`
 * is carried by both and editable by NEITHER — it is in the draft, in the wire
 * body and in the schema's bounds, and no organization page renders a control
 * for it either. This page matches that exactly rather than silently adding one:
 * a field a program could set and an organization could not would be the same
 * divergence in the other direction.
 *
 * NOT CARRIED, AND NOT POLICY: three cards on Privacy & retention state facts
 * that are the same at either scope and are not configuration — who may read
 * location data (`location:read` / `location_history:view`, a permission story
 * with no per-program form), the workforce-vs-EVV separation, and the
 * "what is collected, and when" summary, whose whole content on this page is the
 * controls themselves. Repeating them per program would be three more places for
 * the same sentence to go stale, which is the argument this feature's copy file
 * makes about itself. The RETENTION WINDOW — the one genuinely per-scope thing
 * on that page, and the one the purge sweep reads per program — is here.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THE PROGRAM'S NAME IS A HARD REQUIREMENT
 * ═══════════════════════════════════════════════════════════════════════════
 * Every consequence sentence on this page names the program — "this applies to
 * everyone working in X as soon as you save". Without the name the honest
 * sentence is the organization's, and the organization's is FALSE here: it
 * would tell an operator that a tracking change reaches every worker in the
 * tenant when it reaches one program's. So a failed program read renders the
 * failure and no form, rather than a form whose copy over-claims. The read is
 * `GET /programs/:id` (`program:read`), which the override list this page is
 * reached from already needs.
 */
export function ProgramPolicyPage() {
  const { programId } = useParams({ strict: false }) as { programId: string };
  const navigate = useNav();
  const form = usePolicyForm(programId);
  const program = useQuery({
    queryKey: ['programs', 'detail', programId],
    queryFn: () => programs.detail(programId),
  });

  const name = program.data?.name;
  const audience = name === undefined ? null : programAudience(name);

  return (
    <LocationSettingsShell active="general" dirty={form.dirty}>
      <SettingsCard>
        <a
          href="/settings/location"
          className="rn-muted"
          onClick={(e) => {
            e.preventDefault();
            navigate('/settings/location');
          }}
        >
          {PROGRAM_BACK_LABEL}
        </a>
      </SettingsCard>

      {form.loading || program.isLoading ? (
        <SettingsCard>
          <SkeletonSet surface="table" rows={5} />
        </SettingsCard>
      ) : program.isError || name === undefined || audience === null ? (
        <SettingsCard>
          {/*
            NO FORM WITHOUT THE NAME. See the header: every sentence below is
            about "everyone working in <program>", and the fallback sentence is
            a claim about the whole organization.
          */}
          <LoadFailure
            error={program.error}
            mainRead
            message="We couldn’t load that program just now."
            onRetry={() => void program.refetch()}
          />
        </SettingsCard>
      ) : form.loadError || !form.draft || !form.policy ? (
        <SettingsCard>
          <LoadFailure
            error={form.loadFailure}
            message="We couldn’t load the policy just now."
            onRetry={form.refetch}
          />
        </SettingsCard>
      ) : (
        <>
          <SettingsCard>
            <h2 className="rn-card-title">{programPolicyHeading(name)}</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6) ' }}>
              {programScopeNote(name)}
            </p>
            {/*
              WHETHER THIS PROGRAM HAS A POLICY AT ALL, from the SERVER's own
              `source` and never inferred from the values. A program that
              follows the organization and one that overrides it with the same
              values are indistinguishable by value and are different facts: the
              first stops following the moment the organization changes, the
              second never did.
            */}
            {form.policy.source !== 'program' ? (
              <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }} role="note">
                {programInheritsNote(name, form.policy.source)}
              </p>
            ) : null}
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">How location works on shifts</h2>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-12)' }}>
              <ModeCards
                value={form.draft.mode}
                onChange={(mode) => form.set({ mode })}
                disabled={!form.canManage}
              />

              {form.draft.mode === 'REQUIRE_GEOFENCE' ? (
                <div style={{ display: 'grid', gap: 'var(--space-8)' }}>
                  <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={form.draft.allowAttestedOverride}
                      disabled={!form.canManage}
                      onChange={(e) => form.set({ allowAttestedOverride: e.target.checked })}
                    />
                    <span>{ATTESTED_OVERRIDE_LABEL}</span>
                  </label>
                  {!form.draft.allowAttestedOverride ? (
                    <ConsequenceNote text={ATTESTED_OVERRIDE_OFF_WARNING} />
                  ) : null}
                </div>
              ) : null}

              {/*
                THE SAME BRANCHING NOTE THE GENERAL PAGE RENDERS, with THIS
                page's audience. "Everyone in your organization" would be a
                straightforward over-claim here, which is why `stricterModeNote`
                takes the audience and has no default.
              */}
              {STRICTER_MODES.has(form.draft.mode) &&
              form.draft.mode !== form.policy.mode &&
              scheduleStatementOf(form.draft) !== null ? (
                <ConsequenceNote
                  text={stricterModeNote(scheduleStatementOf(form.draft)!, audience)}
                />
              ) : null}
            </div>
          </SettingsCard>

          {form.draft.mode === 'OFF' ? (
            <SettingsCard>
              <p className="rn-muted" role="status">
                {MODE_OFF_TRACKING_NOTE}
              </p>
            </SettingsCard>
          ) : null}

          <SettingsCard>
            <h2 className="rn-card-title">At the clock punch</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              When collection happens: {CLOCK_COLLECTION_WHEN}.
            </p>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.clockInLocationRequired}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ clockInLocationRequired: e.target.checked })}
                />
                <span>Ask the device for a location at clock-in</span>
              </label>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.clockOutLocationRequired}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ clockOutLocationRequired: e.target.checked })}
                />
                <span>Ask the device for a location at clock-out</span>
              </label>
              <p className="rn-muted" style={{ fontSize: 13 }}>
                {CLOCK_REQUIRED_HONESTY}
              </p>
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">While a shift is running</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              When collection happens: {BREADCRUMB_COLLECTION_WHEN}.
            </p>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.breadcrumbEnabled}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ breadcrumbEnabled: e.target.checked })}
                />
                <span>Collect location points during shifts</span>
              </label>

              {form.draft.breadcrumbEnabled &&
              !form.policy.breadcrumbEnabled &&
              scheduleStatementOf(form.draft) !== null ? (
                <ConsequenceNote
                  text={breadcrumbsOnWarning(scheduleStatementOf(form.draft)!, audience)}
                />
              ) : null}
              {!form.draft.breadcrumbEnabled && form.policy.breadcrumbEnabled ? (
                <ConsequenceNote text={BREADCRUMBS_OFF_WARNING} />
              ) : null}

              <SettingsField
                label="Seconds between points"
                hint="Between 30 seconds and 24 hours (86,400 seconds)."
                error={validateDraft(form.draft).breadcrumbIntervalSeconds}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.draft.breadcrumbIntervalSeconds}
                  disabled={!form.canManage || !form.draft.breadcrumbEnabled}
                  onChange={(e) => form.set({ breadcrumbIntervalSeconds: e.target.value })}
                  style={{ maxWidth: 120 }}
                />
              </SettingsField>

              {form.draft.breadcrumbEnabled && form.valid ? (
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  On the worker’s More screen, their policy card reads: “
                  {workerBreadcrumbCardQuote(Number(form.draft.breadcrumbIntervalSeconds))}”
                </p>
              ) : null}
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">When a punch records an exception</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              {EXCEPTION_INTEGRITY_SENTENCE}
            </p>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.exceptionReasonRequired}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ exceptionReasonRequired: e.target.checked })}
                />
                <span>{EXCEPTION_REASON_LABEL}</span>
              </label>
              {form.draft.exceptionReasonRequired ? (
                <ConsequenceNote text={EXCEPTION_REASON_ON_NOTE} />
              ) : null}

              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={form.draft.exceptionApprovalRequired}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ exceptionApprovalRequired: e.target.checked })}
                />
                <span>{EXCEPTION_APPROVAL_LABEL}</span>
              </label>
              {form.draft.exceptionApprovalRequired ? (
                <ConsequenceNote text={EXCEPTION_APPROVAL_ON_NOTE} />
              ) : (
                <p className="rn-muted" style={{ fontSize: 13 }}>
                  {EXCEPTION_APPROVAL_OFF_NOTE}
                </p>
              )}
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">How long it is kept</h2>
            <div style={{ marginTop: 'var(--space-12)', display: 'grid', gap: 'var(--space-10)' }}>
              <SettingsField
                label="Days location points are kept"
                hint="Between 1 and 3,650 days. Then they are permanently deleted."
                error={validateDraft(form.draft).breadcrumbRetentionDays}
              >
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.draft.breadcrumbRetentionDays}
                  disabled={!form.canManage}
                  onChange={(e) => form.set({ breadcrumbRetentionDays: e.target.value })}
                  style={{ maxWidth: 120 }}
                />
              </SettingsField>
              <p className="rn-muted" style={{ fontSize: 13 }}>
                {PURGE_SWEEP_SENTENCE}
              </p>
              <details>
                <summary className="rn-muted" style={{ cursor: 'pointer', fontSize: 13 }}>
                  Advanced details
                </summary>
                <p className="rn-muted" style={{ fontSize: 13, marginTop: 'var(--space-4)' }}>
                  The sweep runs as the system job <code>{PURGE_JOB_NAME}</code>. This program’s
                  window is what its own points are purged on.
                </p>
              </details>
            </div>
          </SettingsCard>

          {/*
            THE WHOLE SCHEDULE AND RELATIONSHIP SURFACE, unchanged and
            unduplicated. The four relationships, the reason field, the
            no-default rule, the two floors, the saved report and the resulting
            governing timeline are REVREL's and SCHEDUI's; this page states its
            scope and its audience and asks for nothing else.
          */}
          <SettingsCard>
            <PolicySchedule form={form} audience={audience} />
          </SettingsCard>

          {form.canManage ? (
            <SettingsCard>
              <SettingsActions
                dirty={form.dirty}
                valid={form.valid}
                saving={form.saving}
                saved={form.saved}
                error={form.saveError}
                onSave={form.save}
                onCancel={form.cancel}
                saveLabel={PROGRAM_SAVE_LABEL}
              />
            </SettingsCard>
          ) : null}
        </>
      )}
    </LocationSettingsShell>
  );
}

/** The path one program's policy editor lives at. One definition, three readers. */
export function programPolicyHref(programId: string): string {
  return `/settings/location/programs/${programId}`;
}

/** Re-exported so a guard can assert the page reads the scope it displays. */
export const PROGRAM_POLICY_ROUTE = '/settings/location/programs/$programId';
